import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      const msg = apiError(err, "Unable to sign in.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy-deep">
      {/* Brand / imagery */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.pexels.com/photos/32510491/pexels-photo-32510491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1200"
          alt="Event videography"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/70 to-navy-deep/30" />
        <div className="absolute bottom-0 left-0 p-12">
          <Logo chip height={52} className="mb-6" />
          <h1 className="font-display text-5xl xl:text-6xl leading-none text-ivory max-w-md">
            Turn your event moments into cinematic reels.
          </h1>
          <p className="text-muted-foreground mt-5 max-w-sm text-base">
            The private studio for creating and sharing highlight reels, social videos and GIFs.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8">
            <Logo chip height={44} />
          </div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold mb-3">
            <Lock className="w-3.5 h-3.5" /> Staff access only
          </div>
          <h2 className="font-display text-4xl text-ivory mb-2">Reel Maker</h2>
          <p className="text-muted-foreground mb-8 text-sm">Sign in to your private studio dashboard.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-champagne">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@clydeside.events"
                className="bg-navy-card border-navy-border h-12 text-ivory"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-champagne">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-navy-card border-navy-border h-12 text-ivory"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-4 py-3">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold text-base"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-8">
            This is a private internal tool. There is no public sign-up.
          </p>
        </div>
      </div>
    </div>
  );
}
